import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { topicsApi, projectsApi, documentationApi } from "../../../services/api"
import Button from "../../../components/ui/Button"
import Card, { CardHeader, CardBody } from "../../../components/ui/Card"
import { Field, Input, Select } from "../../../components/ui/Field"
import PageHeader from "../../../components/dashboard/PageHeader"
import { useTopicGenerationStatus } from "./useTopicGenerationStatus"

const SavedIdeas = ({ setPage, onViewTopic = () => {}, onGenerateDocFromTopic }) => {
  const [savedTopics, setSavedTopics] = useState([])
  const [projects, setProjects] = useState([])
  const [documentations, setDocumentations] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [category, setCategory] = useState("")
  const [difficulty, setDifficulty] = useState("")

  const fetchMyTopics = async () => {
    try {
      const [topicsRes, projectsRes, docsRes] = await Promise.all([
        topicsApi.list({ mine: 'true', limit: 100 }),
        projectsApi.list({}),
        documentationApi.list({})
      ])
      if (topicsRes?.data?.data?.topics) setSavedTopics(topicsRes.data.data.topics)
      if (projectsRes?.data?.data?.projects) setProjects(projectsRes.data.data.projects)
      if (docsRes?.data?.data) setDocumentations(docsRes.data.data)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const { shouldHideGenerate, getTopicStatusLabel, getProjectLink } = useTopicGenerationStatus(projects, documentations)

  useEffect(() => {
    fetchMyTopics()
  }, [])

  const categoryOptions = Array.from(
    new Set(savedTopics.map((t) => (t?.category || "").trim()).filter(Boolean))
  ).sort()

  const difficultyOptions = Array.from(
    new Set(savedTopics.map((t) => (t?.difficulty || "").trim()).filter(Boolean))
  ).sort()

  const normalizedSearch = search.trim().toLowerCase()
  const filteredTopics = savedTopics.filter((t) => {
    if (category && (t?.category || "") !== category) return false
    if (difficulty && (t?.difficulty || "") !== difficulty) return false

    if (normalizedSearch) {
      const haystack = `${t?.title || ""} ${t?.description || ""} ${t?.summary || ""}`.toLowerCase()
      return haystack.includes(normalizedSearch)
    }

    return true
  })

  const clearFilters = () => {
    setSearch("")
    setCategory("")
    setDifficulty("")
  }

  return (
    <div className="flex flex-col w-full">
      <PageHeader
        eyebrow="Topic ideas"
        title="Saved ideas"
        description="Topics you saved. Open one to view details or generate project documentation."
        action={
          <Button size="sm" onClick={() => setPage("TopicIdeas")} className="gap-2">
            Generate ideas
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          </Button>
        }
      />

      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
        <Field label="Search" className="flex-1">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title, summary or keywords"
          />
        </Field>

        <Field label="Category" className="sm:w-48">
          <Select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All categories</option>
            {categoryOptions.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </Select>
        </Field>

        <Field label="Difficulty" className="sm:w-48">
          <Select value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
            <option value="">All difficulties</option>
            {difficultyOptions.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </Select>
        </Field>

        <Button
          size="sm"
          variant="ghost"
          className="shrink-0"
          onClick={clearFilters}
          disabled={!search && !category && !difficulty}
        >
          Clear
        </Button>
      </div>
      {loading ? (
        <p className="text-slate-600 mt-4">Loading...</p>
      ) : (
        <Card className="mt-4 shadow-soft">
          <CardHeader
            title="Your saved topics"
            subtitle="Open a topic to see details, or generate project documentation."
          />
          <CardBody className="pt-2">
            {savedTopics.length === 0 && (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
                No saved topics yet. Generate topics to get started.
              </div>
            )}
            {savedTopics.length > 0 && filteredTopics.length === 0 && (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
                No saved topics match your filters. Try adjusting `Search`, `Category`, or `Difficulty`.
              </div>
            )}

            <div className="flex flex-col gap-3">
              {filteredTopics.map((t, index) => (
                <div
                  key={t._id}
                  className="group flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 hover:bg-slate-50 hover:border-slate-300 transition-colors"
                >
                  <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-slate-100 px-2 text-xs font-semibold text-slate-700">
                    {index + 1}
                  </span>
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => onViewTopic(t)}
                  >
                    <p className="font-semibold text-slate-900 group-hover:text-slate-950 transition-colors">
                      {t.title}
                    </p>
                    {t.description && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{t.description}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {onGenerateDocFromTopic && !shouldHideGenerate(t._id) ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation()
                          onGenerateDocFromTopic(t._id, t)
                        }}
                      >
                        Generate project documentation
                      </Button>
                    ) : null}
                    {getTopicStatusLabel(t._id) && getProjectLink(t._id) ? (
                      <Link
                        to={`/dashboard/project?projectId=${encodeURIComponent(String(getProjectLink(t._id)))}`}
                        className="text-xs font-semibold text-emerald-700 hover:text-emerald-800"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {getTopicStatusLabel(t._id)} →
                      </Link>
                    ) : null}
                    <button
                      type="button"
                      className="text-sm font-semibold text-slate-600 hover:text-emerald-700 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation()
                        onViewTopic(t)
                      }}
                    >
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  )
}

export default SavedIdeas
